import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useAuth from "../useAuth";
import {
  FaSlidersH,
  FaTags,
  FaImages,
  FaQuoteLeft,
  FaUsers,
  FaCalendarCheck,
  FaCrown,
  FaUserCircle,
  FaSignOutAlt,
  FaGem,
} from "react-icons/fa";

export default function NavBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname?.split("/")?.[2] || "";

  const menuItems = [
    {
      section: "Content Management",
      items: [
        { title: "Carousel Manager", path: "carouselManager", icon: <FaSlidersH size={15} />, roles: ["admin", "telecaller"] },
        { title: "Category Manager", path: "categoryManager", icon: <FaTags size={15} />, roles: ["admin"] },
        { title: "Gallery Manager", path: "galleryManager", icon: <FaImages size={15} />, roles: ["admin"] },
        { title: "Testimonial Manager", path: "testimonialManager", icon: <FaQuoteLeft size={15} />, roles: ["admin"] },
        { title: "Pricing Manager", path: "pricingManager", icon: <FaCrown size={15} />, roles: ["admin"] },
      ],
    },
    {
      section: "Bookings & Users",
      items: [
        { title: "Booking Requests", path: "bookingRequests", icon: <FaCalendarCheck size={15} />, roles: ["admin"] },
        { title: "Users Management", path: "usersManagement", icon: <FaUsers size={15} />, roles: ["admin"] },
      ],
    },
    {
      section: "Account Settings",
      items: [
        { title: "My Profile", path: "profile", icon: <FaUserCircle size={15} />, roles: ["admin", "user", "telecaller"] },
      ],
    },
  ];

  return (
    <div className="bg-[#121110] text-[#F8F5F2] h-full min-h-screen flex flex-col justify-between border-r border-[#C8A165]/15 select-none">
      {/* Top Logo / Brand Header */}
      <div>
        <div className="p-5 border-b border-[#C8A165]/15 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-[#C8A165] to-[#F5E5C9] flex items-center justify-center text-[#121110] shadow-md shadow-[#C8A165]/20 font-bold">
            <FaGem size={20} />
          </div>
          <div>
            <h1 className="font-cormorant font-bold text-lg text-[#F8F5F2] leading-none tracking-wide">
              Tanushree <span className="text-[#C8A165] italic">Saha</span>
            </h1>
            <p className="font-poppins text-[0.62rem] text-[#C8A165]/70 tracking-widest uppercase mt-1">
              Admin Portal
            </p>
          </div>
        </div>

        {/* Navigation Sections */}
        <div className="px-3 py-4 space-y-6">
          {menuItems.map((group, gIdx) => (
            <div key={gIdx}>
              <h3 className="px-3 text-[0.6rem] font-poppins font-semibold uppercase tracking-[0.2em] text-[#C8A165]/60 mb-2">
                {group.section}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => {
                  if (!item.roles.includes(user?.userType)) return null;
                  const isActive = currentPath === item.path;

                  return (
                    <button
                      key={item.path}
                      onClick={() => navigate(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl font-poppins text-xs transition-all duration-200 ${
                        isActive
                          ? "bg-gradient-to-r from-[#C8A165]/20 to-transparent text-[#C8A165] font-semibold border-l-2 border-[#C8A165] shadow-sm"
                          : "text-cream/70 hover:bg-white/[0.04] hover:text-[#C8A165]"
                      }`}
                    >
                      <span className={isActive ? "text-[#C8A165]" : "text-cream/50"}>
                        {item.icon}
                      </span>
                      <span className="truncate">{item.title}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer User Info & Logout */}
      <div className="p-4 border-t border-[#C8A165]/15 bg-[#0D0C0B]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5 overflow-hidden">
            <div className="w-8 h-8 rounded-full bg-[#C8A165]/20 border border-[#C8A165]/40 flex items-center justify-center text-[#C8A165] font-bold text-xs uppercase">
              {user?.name?.[0] || "A"}
            </div>
            <div className="truncate">
              <p className="font-poppins text-xs font-semibold text-cream truncate capitalize">
                {user?.name || "Admin User"}
              </p>
              <p className="font-poppins text-[0.6rem] text-[#C8A165] tracking-wider uppercase font-medium">
                {user?.userType || "Admin"}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to logout?")) {
                logout();
                navigate("/login");
              }
            }}
            className="p-2 text-cream/50 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            title="Logout"
          >
            <FaSignOutAlt size={15} />
          </button>
        </div>
      </div>
    </div>
  );
}
